import { PaymentInterface } from "@/interfaces";

type PaymentMeta = PaymentInterface["meta"];

/** Asked for on every card, whichever address it is billed to. */
const ALWAYS_REQUIRED = ["card_holder_name"] as const;

/**
 * Asked for only when the card is billed somewhere other than the service
 * address, in the order the form shows them.
 *
 * Address line 2 is left out on purpose: most addresses have no apartment,
 * suite or unit, and requiring one left those customers typing "N/A" to get
 * past the payment step (CORE-2045).
 */
const REQUIRED_FOR_OTHER_ADDRESS = [
  "billing_address_line1",
  "billing_city",
  "billing_postal_code",
  "billing_country",
  "billing_province",
] as const;

export type RequiredPaymentField =
  | (typeof ALWAYS_REQUIRED)[number]
  | (typeof REQUIRED_FOR_OTHER_ADDRESS)[number];

const isBlank = (value?: string): boolean => !value || value.trim() === "";

/** The required payment fields the customer left blank, in form order. */
export const blankRequiredPaymentFields = (
  meta: Partial<PaymentMeta> | undefined,
  sameAddress: boolean
): RequiredPaymentField[] => {
  const required: readonly RequiredPaymentField[] = sameAddress
    ? ALWAYS_REQUIRED
    : [...ALWAYS_REQUIRED, ...REQUIRED_FOR_OTHER_ADDRESS];

  return required.filter((field) => isBlank(meta?.[field]));
};

/**
 * The typed billing address as the single line the payment gateway takes. Line
 * 2 goes in when the customer entered one and leaves no gap when they did not.
 */
export const composeBillingAddress = (
  meta: Partial<PaymentMeta> | undefined
): string =>
  [
    meta?.billing_address_line1,
    meta?.billing_address_line2,
    meta?.billing_city,
    meta?.billing_postal_code,
    meta?.billing_province,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
